import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { startOfToday, endOfDay, addDays, startOfDay } from 'date-fns';
import Toast from 'react-native-toast-message';

import { CardLayout, showApolloErrorToUser } from '@pletly/ui';

import { withTranslation } from '../../../../locales/hoc';
import { useAddTemporaryConnectionMutation } from './components/__generated__/addTemporaryConnection.mutation.generated';
import { RootStackScreenProps } from '../../../../navigation';
import { GetOrganisationDocument } from '../../../view/components/__generated__/organisation.generated';
import { FormData, TemporaryAccessForm } from './components/TemporaryAccessForm';
import { useOrganisationData } from '../../../view/components/useOrganisationData';

export interface NewTemporaryAccessScreenParams {
	organisationUuid: string;
	memberId?: string;
}

type Props = RootStackScreenProps<'NewTemporaryAccess'>;

export const NewTemporaryAccess = withTranslation(
	({
		route: {
			params: { organisationUuid, memberId },
		},
	}: Props) => {
		const { t } = useTranslation();

		const { organisation } = useOrganisationData({ uuid: organisationUuid });

		const [addTemporaryConnection, { loading: saving }] = useAddTemporaryConnectionMutation({
			refetchQueries: [{ query: GetOrganisationDocument, variables: { uuid: organisationUuid } }],
		});

		const submitForm = useCallback(
			async (data: FormData, resetValues: () => void) => {
				if (saving) return;

				const { startDate, endDate } = extractDates(data);
				const { email, careRecipients } = data;

				const careRecipientsMembersId = memberId ? [memberId] : careRecipients;
				if (!careRecipientsMembersId?.length) {
					return Toast.show({
						type: 'error',
						text1: t('toasts.error.no-care-recipients-selected'),
					});
				}

				try {
					await addTemporaryConnection({
						variables: {
							data: {
								organisationUuid,
								careRecipientsMembersId: careRecipientsMembersId.map(Number),
								caregiverEmail: email,
								startDate,
								endDate,
							},
						},
					});
					resetValues();
					Toast.show({
						type: 'success',
						text1: t('toasts.success.temporary-access-invite-sent'),
					});
				} catch (error) {
					showApolloErrorToUser(error as Error);
				}
			},
			[addTemporaryConnection, memberId, organisationUuid, t, saving],
		);

		return (
			<CardLayout title={t('temporary-access.title')}>
				<TemporaryAccessForm
					onSubmit={submitForm}
					saving={saving}
					careRecipientsToSelect={organisation?.members.careRecipients || []}
					withCareRecipientsSelect={!memberId}
				/>
			</CardLayout>
		);
	},
);

const extractDates = (formData: FormData) => {
	const { accessDuration } = formData;
	if (accessDuration === 'custom') {
		const { customAccessDuration } = formData;

		return {
			startDate: startOfDay(customAccessDuration.startDate),
			endDate: endOfDay(customAccessDuration.endDate),
		};
	}

	const startDate = startOfToday();
	const endDate = endOfDay(addDays(startDate, Number(accessDuration)));

	return { startDate, endDate };
};
